import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATUS = {
  draft: ["Draft", "bg-amber-100 text-amber-700"],
  sent: ["Sent", "bg-blue-100 text-blue-700"],
  viewed: ["Reviewing", "bg-sky-100 text-sky-700"],
  accepted: ["Accepted", "bg-green-100 text-green-700"],
  declined: ["Declined", "bg-red-100 text-red-700"],
  expired: ["Expired", "bg-zinc-200 text-zinc-600"],
};

function SignaturePad({ onChange }) {
  const ref = useRef(null);

  const paint = () => {
    const canvas = ref.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 320;
    const height = canvas.clientHeight || 160;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#18181b";
    onChange("");
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => paint());
    return () => cancelAnimationFrame(frame);
  }, []);

  const point = (event) => {
    const rect = ref.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const down = (event) => {
    const ctx = ref.current.getContext("2d");
    const start = point(event);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ref.current.setPointerCapture(event.pointerId);
    ref.current.dataset.drawing = "1";
  };

  const move = (event) => {
    if (ref.current.dataset.drawing !== "1") return;
    const ctx = ref.current.getContext("2d");
    const next = point(event);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
  };

  const up = () => {
    if (ref.current.dataset.drawing !== "1") return;
    ref.current.dataset.drawing = "0";
    const blank = isBlank(ref.current);
    onChange(blank ? "" : ref.current.toDataURL("image/png"));
  };

  return (
    <div>
      <canvas
        ref={ref}
        data-testid="estimate-sign-pad"
        className="w-full h-40 rounded-xl border border-zinc-300 bg-white touch-none"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      />
      <button type="button" data-testid="estimate-sign-clear" onClick={paint} className="mt-2 text-xs font-bold text-zinc-500">Clear signature</button>
    </div>
  );
}

function isBlank(canvas) {
  const ctx = canvas.getContext("2d");
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < data.length; i += 16) {
    if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return false;
  }
  return true;
}

export default function EstimateShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");
  const [reason, setReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/e/${token}`).then((response) => setData(response.data)).catch((err) => {
      setError(err.response?.data?.detail || "Estimate not found.");
    });
  }, [token]);

  if (error) return <div className="p-10 text-center text-zinc-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const est = data.estimate;
  const biz = data.business || {};
  const [statusLabel, statusColor] = STATUS[est.status] || ["", "bg-zinc-100 text-zinc-600"];
  const locked = est.locked || est.status === "accepted";
  const pdfUrl = `${api.defaults.baseURL}/e/${token}/pdf`;

  const accept = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/e/${token}/accept`, {
        signer_name: name.trim(),
        signature_png: signature,
        agreed,
      });
      setData(response.data);
      toast.success("Estimate accepted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept that");
    }
    setBusy(false);
  };

  const decline = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/e/${token}/decline`, { reason: reason.trim() });
      setData(response.data);
      toast.success("Estimate declined");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't decline that");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen p-4 pb-10" data-testid="estimate-share-page">
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="bg-zinc-900 text-white p-5 flex justify-between items-start gap-3">
          <div>
            <div className="flex items-center gap-2">
              {biz.logo_url && <img src={FILE_URL(biz.logo_url)} alt="" className="h-9 w-9 rounded-md object-cover bg-white" />}
              <p className="font-heading font-black text-xl">{biz.business_name || "Estimate"}</p>
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">{[biz.phone, biz.email].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="text-right">
            <p className="font-heading font-black text-orange-500 text-lg">ESTIMATE</p>
            <p className="text-xs text-zinc-400">{est.estimate_number}</p>
          </div>
        </div>
        <div className="p-5">
          <div className="flex justify-between text-sm mb-4 gap-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400">Prepared for</p>
              <p className="font-semibold text-zinc-900">{est.customer_name || "—"}</p>
              <p className="text-zinc-500 text-xs">{est.location}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-bold text-zinc-400">Valid until</p>
              <p className="font-semibold text-zinc-900">{est.valid_until || "—"}</p>
              {statusLabel && <span data-testid="estimate-status" className={`inline-block mt-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>}
            </div>
          </div>
          <div className="space-y-2.5 border-t border-zinc-100 pt-3">
            {(est.line_items || []).map((line, index) => (
              <div key={index} className="flex justify-between gap-3 text-sm">
                <p className="text-zinc-800">{line.description}</p>
                <p className="font-mono-num font-semibold shrink-0">{money(line.amount)}</p>
              </div>
            ))}
            {(est.materials || []).map((mat, index) => (
              <div key={`m${index}`} className="flex justify-between gap-3 text-sm">
                <p className="text-zinc-800">{mat.name} <span className="text-xs text-zinc-400">(materials)</span></p>
                <p className="font-mono-num font-semibold shrink-0">{money(mat.amount)}</p>
              </div>
            ))}
            {est.customer_supplied?.length > 0 && (
              <p className="text-[11px] text-zinc-400">You supply: {est.customer_supplied.join(", ")}</p>
            )}
          </div>
          <div className="border-t border-zinc-100 mt-4 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-zinc-500"><span>Subtotal</span><span className="font-mono-num">{money(est.subtotal)}</span></div>
            {est.discount > 0 && <div className="flex justify-between text-sm text-zinc-500"><span>Discount</span><span className="font-mono-num">- {money(est.discount)}</span></div>}
            {est.tax > 0 && <div className="flex justify-between text-sm text-zinc-500"><span>Tax ({est.tax_rate}%)</span><span className="font-mono-num">{money(est.tax)}</span></div>}
            <div className="flex justify-between items-center pt-2">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading font-black text-2xl text-orange-600" data-testid="estimate-total">{money(est.total)}</span>
            </div>
          </div>
          {est.notes && <p className="text-xs text-zinc-500 mt-4 border-t border-zinc-100 pt-3">{est.notes}</p>}
        </div>
      </div>

      <a href={pdfUrl} data-testid="estimate-download" className="mt-3 h-11 rounded-xl border border-zinc-200 bg-white flex items-center justify-center gap-2 text-sm font-bold text-zinc-800">
        <DownloadSimple size={18} weight="bold" /> Download PDF
      </a>

      {locked && est.signature && (
        <div data-testid="estimate-signed-banner" className="mt-4 bg-green-50 border border-green-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-bold text-green-700">Accepted</p>
          <p className="font-heading font-black text-xl text-zinc-900 mt-1">{est.signature.signer_name}</p>
          <p className="text-xs text-zinc-500 mt-1">{est.signature.signed_at ? new Date(est.signature.signed_at).toLocaleString() : ""}</p>
          {est.signature.signature_png && <img src={est.signature.signature_png} alt="Signature" className="mt-3 h-20 bg-white border border-green-100 rounded-lg" />}
          <p className="text-[11px] text-zinc-400 mt-2 break-all">Document hash {est.signature.content_hash}</p>
        </div>
      )}

      {est.status === "declined" && (
        <div data-testid="estimate-declined-banner" className="mt-4 bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-bold text-red-700">Declined</p>
          <p className="text-sm text-zinc-700 mt-1">{est.decline_reason || "No reason given."}</p>
        </div>
      )}

      {est.status === "expired" && (
        <p className="mt-4 text-sm text-zinc-500 text-center">This estimate has expired. Ask {biz.business_name || "your contractor"} for a new one.</p>
      )}

      {!locked && est.status !== "declined" && est.status !== "expired" && (
        <div className="mt-4 bg-white border border-zinc-200 rounded-2xl p-4 space-y-3" data-testid="estimate-sign-card">
          <p className="font-heading font-black text-lg">Accept this estimate</p>
          <label className="block">
            <span className="text-[11px] uppercase font-bold text-zinc-400">Full name</span>
            <Input data-testid="estimate-sign-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pat Homeowner" className="h-12 mt-1" />
          </label>
          <div>
            <p className="text-[11px] uppercase font-bold text-zinc-400 mb-1">Signature</p>
            <SignaturePad onChange={setSignature} />
          </div>
          <label className="flex items-start gap-2 text-sm text-zinc-700">
            <input data-testid="estimate-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 accent-orange-600" />
            <span>I agree this electronic signature is legally binding and I accept this estimate.</span>
          </label>
          <Button data-testid="estimate-accept-btn" onClick={accept} disabled={busy || !agreed || !signature || name.trim().length < 3} className="w-full h-12 font-bold">
            {busy ? "Sending…" : "Accept estimate"}
          </Button>
          {!declining ? (
            <button data-testid="estimate-decline-btn" type="button" onClick={() => setDeclining(true)} className="w-full h-11 text-sm font-bold text-zinc-500">Decline</button>
          ) : (
            <div className="space-y-2">
              <Textarea data-testid="estimate-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Optional reason" />
              <Button data-testid="estimate-decline-confirm" variant="outline" onClick={decline} disabled={busy} className="w-full h-11 font-bold">Confirm decline</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
